// src/pages/Home.jsx
import React from "react";
import {
  Box,
  Container,
  Stack,
  Heading,
  Text,
  SimpleGrid,
  Badge,
  Button,
  HStack,
  Separator,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { Phone } from "lucide-react";
import { companies } from "../../data/companies";
import CompanyCard from "../content/companylist/CompanyCard";
import ResponsiveContact from "../content/forms/ResponsiveContact";
import AboutIntro from "../content/home/AboutIntro";
import MethodologyStrip from "../content/home/MethodologyStrip";
import ProcessGraphic from "../content/home/ProcessGraphic";
import GenericContactForm from "../content/forms/GenericContactForm";

export default function Home() {
  const subtle = { base: "gray.700", _dark: "gray.300" };
  const cardBg = { base: "white", _dark: "gray.800" };
  const border = { base: "gray.200", _dark: "whiteAlpha.300" };
  const heroOverlay = "rgba(0,0,0,0.55)";
  const heroBg = "/images/companies-bg.png";
  return (
    <Box>
      {/* HERO */}
      <Box
        position="relative"
        bgImage="url('/images/home-bg.png')"
        bgPos={{ base: "center top", md: "center" }}
        bgSize="cover"
        bgRepeat="no-repeat"
        minH={{ base: "60vh", md: "60vh", lg: "60vh" }} // ⬅️ taller hero
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          bg: heroOverlay,
        }}
      >
        <Container
          maxW="6xl"
          py={{ base: 10, md: 16 }}
          position="relative"
          zIndex={1}
          color="white"
        >
          <Stack
            direction={{ base: "column", lg: "row" }}
            spacing={{ base: 8, lg: 12 }}
            align="flex-start"
          >
            {/* Left: Title + blurb */}
            <Box flex="1" pr={{ lg: 8 }}>
              <Heading
                fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
                lineHeight={{ base: "1.15", md: "1.1" }}
                fontWeight="800"
                letterSpacing={{ base: "-0.01em", md: "-0.02em" }}
                mb={3}
              >
                Honest, data-driven tax relief reviews.
              </Heading>

              <Text
                fontSize={{ base: "lg", md: "xl" }}
                opacity={0.95}
                maxW="2xl"
                mb={4}
              >
                We’re <strong>Consumer Advocate Reviews</strong>—here to give
                you clear, unbiased guidance on tax relief. We compare leading
                firms, explain the trade-offs, and help you match with the right
                team for your situation.
              </Text>

              <HStack spacing={3} mt={2} wrap="wrap">
                <Badge
                  colorScheme="blue"
                  variant="solid"
                  fontSize={{ base: "sm", md: "md" }}
                >
                  Editorial independence
                </Badge>
                <Badge
                  colorScheme="green"
                  variant="solid"
                  fontSize={{ base: "sm", md: "md" }}
                >
                  Transparent methodology
                </Badge>
                <Badge
                  colorScheme="purple"
                  variant="solid"
                  fontSize={{ base: "sm", md: "md" }}
                >
                  Real case studies
                </Badge>
              </HStack>
            </Box>

            {/* Right: Contact form card */}
            <Box>
              {/* ✅ Drop-in form (context-aware) */}
              <GenericContactForm />
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* HOW IT WORKS */}
      <Container maxW="6xl" py={{ base: 10, md: 14 }}>
        <Heading size="lg" mb={2}>
          How our reviews work
        </Heading>
        <Text color={subtle} mb={6}>
          We evaluate companies across outcomes, expertise, transparency, and
          customer experience. Here’s the short version:
        </Text>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={border}
            borderRadius="lg"
            p={6}
          >
            <Badge colorScheme="blue" mb={2}>
              Step 1
            </Badge>
            <Heading size="md" mb={2}>
              Collect the facts
            </Heading>
            <Text color={subtle}>
              We analyze disclosures, fee structures, BBB/AG actions, and real
              client outcomes where available.
            </Text>
          </Box>

          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={border}
            borderRadius="lg"
            p={6}
          >
            <Badge colorScheme="purple" mb={2}>
              Step 2
            </Badge>
            <Heading size="md" mb={2}>
              Score the experience
            </Heading>
            <Text color={subtle}>
              Onboarding speed, communication quality, and clarity of next steps
              matter — especially when the IRS clock is ticking.
            </Text>
          </Box>

          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={border}
            borderRadius="lg"
            p={6}
          >
            <Badge colorScheme="green" mb={2}>
              Step 3
            </Badge>
            <Heading size="md" mb={2}>
              Match to your case
            </Heading>
            <Text color={subtle}>
              Different firms excel at different problems. We steer complex
              corporate, small-biz, or trucker cases to the right fit.
            </Text>
          </Box>
        </SimpleGrid>

        <Separator my={10} />
        <Stack spacing={6} align="center">
          {companies.map((c, i) => (
            <CompanyCard key={c.key} item={c} rank={i + 1} />
          ))}
        </Stack>
      </Container>

      <Box
        as="section"
        bg={{ base: "gray.50", _dark: "gray.900" }}
        borderTopWidth="1px"
      >
        <Container maxW="6xl" py={{ base: 10, md: 14 }}>
          <Stack spacing={{ base: 10, md: 14 }}>
            <AboutIntro />
            <MethodologyStrip />
            <ProcessGraphic />
          </Stack>
        </Container>
      </Box>
      {/* BOTTOM CTA */}
      <Box bg={{ base: "gray.50", _dark: "gray.900" }} borderTopWidth="1px">
        <Container maxW="6xl" py={{ base: 10, md: 14 }}>
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={{ base: 4, md: 8 }}
            align="center"
            justify="space-between"
          >
            <Box>
              <Heading size="md" mb={1}>
                Not sure where to start?
              </Heading>
              <Text color={subtle}>
                Answer a few questions and we’ll suggest a provider based on
                your case profile.
              </Text>
            </Box>

            <HStack>
              <Button
                as={RouterLink}
                to="/tax-relief-calculator"
                variant="outline"
              >
                Try the calculator
              </Button>
              <Button
                as={RouterLink}
                to="/tax-relief-companies"
                colorScheme="blue"
              >
                Browse companies
              </Button>
            </HStack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
